import { every } from 'lodash'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import Page from '@/components/Page'

import messages from './messages'
import SpaceCatalogAppDetailsDescription from './SpaceCatalogAppDetailsDescription'
import SpaceCatalogAppDetailsEdit from './SpaceCatalogAppDetailsEdit'

import './index.scss'

import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom'
import { usePrevious } from 'react-use'

import { getCurrentSpaceId } from '@/components/App/selectors'
import WebHookDisplay from '@/components/CatalogAppInstanceForm/WebHookDisplay'
import DeleteConfirmationWithInput from '@/components/DeleteConfirmationWithInput'
import ErrorConfirmation from '@/components/ErrorConfirmation'
import SuccessConfirmation from '@/components/SuccessConfirmation'
import { RootState } from '@/store'
import { resetRequest } from '@/store/actions'
import { isRequestError, isRequestPending, isRequestSuccessful } from '@/store/selectors'

import {
  addCatalogAppInstance,
  clearCurrentApp,
  clearCurrentCatalogApp,
  deleteCatalogAppInstance,
  fetchCatalogApp,
  fetchCatalogAppInstance,
  updateCatalogAppInstance
} from './actions'
import {
  ADD_CATALOG_APP_INSTANCE,
  DELETE_CATALOG_APP_INSTANCE,
  FETCH_CATALOG_APP,
  FETCH_CATALOG_APP_INSTANCE,
  UPDATE_CATALOG_APP_INSTANCE
} from './actionTypes'
import { CatalogAppDetails, CatalogAppInstance } from './interfaces'
import { getCatalogApp, getCatalogAppInstance } from './selectors'

const errorsToHandle = [
  {
    type: FETCH_CATALOG_APP_INSTANCE,
    data: { detailCode: 'FETCH_CATALOG_APP_INSTANCE' }
  },
  {
    type: FETCH_CATALOG_APP,
    data: { detailCode: 'FETCH_CATALOG_APP' }
  },
  {
    type: ADD_CATALOG_APP_INSTANCE,
    data: { detailCode: 'ADD_CATALOG_APP_INSTANCE' }
  },
  {
    type: DELETE_CATALOG_APP_INSTANCE,
    data: { detailCode: 'DELETE_CATALOG_APP_INSTANCE' }
  },
  {
    type: UPDATE_CATALOG_APP_INSTANCE,
    data: { detailCode: 'UPDATE_CATALOG_APP_INSTANCE' }
  }
]

export interface SpaceCatalogAppDetailsPageProps {
  actions: {
    addCatalogAppInstance: (appToInstall: any, spaceId: string) => void
    clearCurrentApp: () => void
    deleteCatalogAppInstance: (appId: string) => void
    fetchCatalogApp: (spaceId: string, appId: string) => void
    fetchCatalogAppInstance: (appId: string, spaceId: string) => void
    resetRequest: (arr: string[]) => void
    updateCatalogAppInstance: (appInstanceId: string, appToUpdate: any, currentSpaceId: string) => void
    clearCurrentCatalogApp: () => void
  }
  app?: CatalogAppDetails | CatalogAppInstance | null
  appInstance: CatalogAppInstance | null
  currentSpaceId: string
  hasError: boolean
  isDeleteActionInProgress: boolean
  isDeleteSuccessful: boolean
  isInstallSuccessful: boolean
  isLoading?: boolean
  isUpdateActionInProgress: boolean
  isUpdateSuccessful: boolean
}
function SpaceCatalogAppDetailsPage(props: SpaceCatalogAppDetailsPageProps) {
  const history = useHistory()
  const location = useLocation()
  const params = useParams<{ spaceId: string; appId: string }>()
  const match = useRouteMatch()

  const [state, _setState] = useState({
    installView: false,
    showDeleteModal: false
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidMount
  useEffect(() => {
    props.actions.resetRequest([
      ADD_CATALOG_APP_INSTANCE,
      DELETE_CATALOG_APP_INSTANCE,
      UPDATE_CATALOG_APP_INSTANCE,
      FETCH_CATALOG_APP_INSTANCE
    ])
    if (match.path.includes('edit')) {
      props.actions.fetchCatalogAppInstance(params.appId, props.currentSpaceId)
    } else {
      props.actions.fetchCatalogApp(props.currentSpaceId, params.appId)
    }

    if (match.path.includes('install')) {
      setState({ installView: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    let { app } = props
    if (app || props.appInstance) {
      app = app || props.appInstance
      if (app !== prevProps.app && params.appId) {
        const { currentSpaceId } = props
        if (currentSpaceId !== prevProps.currentSpaceId) {
          goBackInstalled()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  // componentWillUnmount
  useEffect(() => {
    return () => {
      props.actions.clearCurrentApp()
      props.actions.clearCurrentCatalogApp()

      props.actions.resetRequest([
        ADD_CATALOG_APP_INSTANCE,
        DELETE_CATALOG_APP_INSTANCE,
        UPDATE_CATALOG_APP_INSTANCE,
        FETCH_CATALOG_APP_INSTANCE
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const installApp = async (appToInstall: CatalogAppInstance) => {
    props.actions.addCatalogAppInstance(appToInstall, props.currentSpaceId)
  }

  const updateApp = async (appInstanceId: string, appToUpdate: CatalogAppInstance) => {
    props.actions.updateCatalogAppInstance(appInstanceId, appToUpdate, props.currentSpaceId)
  }

  const deleteApp = () => setState({ showDeleteModal: true })

  const deleteModal = () => (
    <DeleteConfirmationWithInput
      onConfirm={onConfirmDelete}
      onCancel={() =>
        setState({
          showDeleteModal: false
        })
      }
      itemName={props.appInstance?.overrideAppName || props.appInstance?.name || ''}
      subMessage={<FormattedMessage {...messages.subMessage} />}
      warning={
        <FormattedMessage
          {...messages.deleteConfirm}
          values={{
            appName: (
              <span className="confirm-app-name">{props.appInstance?.overrideAppName || props.appInstance?.name}</span>
            )
          }}
        />
      }
      label={<FormattedMessage {...messages.confirmAction} />}
      cancel={<FormattedMessage {...messages.cancel} />}
      deleteMessage={<FormattedMessage {...messages.delete} />}
      deleteButton={<FormattedMessage {...messages.deleteButton} />}
    />
  )

  const onConfirmDelete = async () => {
    setState({
      showDeleteModal: false
    })
    props.actions.deleteCatalogAppInstance(params.appId)
  }

  const goBack = (count = 1) => {
    const stripDetailsPath = stripPath(location.pathname, count)
    history.push(stripDetailsPath)
  }

  const goBackInstalled = () => {
    history.push(`/space/${params.spaceId}/apps/installed`)
  }

  const toggleInstallView = () => {
    setState({
      installView: !state.installView
    })
  }

  const goToConnectorLogs = () => {
    window.open(
      `${stripPath(window.location.href, 4)}/apps/${props.appInstance?.id}/${props.appInstance?.connectorId}/logs`
    )
  }

  function stripPath(path: string, count: number) {
    for (let i = 0; i < count; i++) {
      path = path.slice(0, Math.max(0, path.lastIndexOf('/')))
    }

    return path
  }

  function generateView() {
    if (props.hasError) {
      return (
        <ErrorConfirmation
          onBack={goBackInstalled}
          error={<FormattedMessage {...messages.appErrorMessage} />}
          onTryAgain={resetErrorState}
          onTryAgainButtonMessage={<FormattedMessage {...messages.tryAgain} />}
          subMessage=""
        />
      )
    } else if (props.isInstallSuccessful) {
      if (
        every([(props.app as CatalogAppDetails).eventModel === 'w', props.appInstance?.connectorConfig?.webHookUrl])
      ) {
        return (
          <SuccessConfirmation
            message={
              <>
                <FormattedMessage {...messages.installSuccess} />
                <WebHookDisplay
                  className="webhook-display"
                  webHookUrl={props.appInstance?.connectorConfig?.webHookUrl}
                />
              </>
            }
            onBack={goBackInstalled}
          />
        )
      }
      return (
        <SuccessConfirmation message={<FormattedMessage {...messages.installSuccess} />} onBack={goBackInstalled} />
      )
    } else if (props.isDeleteSuccessful) {
      return <SuccessConfirmation message={<FormattedMessage {...messages.deleteSuccess} />} onBack={goBackInstalled} />
    } else if (props.isUpdateSuccessful) {
      if (every([props.appInstance?.eventModel === 'w', props.appInstance?.connectorConfig?.webHookUrl])) {
        return (
          <SuccessConfirmation
            message={
              <>
                <FormattedMessage {...messages.updateSuccess} />
                <WebHookDisplay
                  className="webhook-display"
                  webHookUrl={props.appInstance?.connectorConfig?.webHookUrl}
                />
              </>
            }
            onBack={goBackInstalled}
          />
        )
      }
      return <SuccessConfirmation message={<FormattedMessage {...messages.updateSuccess} />} onBack={goBackInstalled} />
    } else if (match.path.includes('edit')) {
      return (
        <SpaceCatalogAppDetailsEdit
          app={props.appInstance as CatalogAppInstance}
          deleteApp={deleteApp}
          goBack={goBackInstalled}
          goToConnectorLogs={goToConnectorLogs}
          installApp={installApp}
          updateApp={updateApp}
          installView={state.installView}
          toggleInstallView={toggleInstallView}
          isUpdateActionInProgress={!!props.isUpdateActionInProgress}
          isDeleteActionInProgress={!!props.isDeleteActionInProgress}
        />
      )
    } else {
      return (
        <SpaceCatalogAppDetailsDescription
          app={props.app as CatalogAppDetails}
          deleteApp={deleteApp}
          goBack={goBack}
          goToConnectorLogs={goToConnectorLogs}
          installApp={installApp}
          updateApp={updateApp}
          installView={state.installView}
          toggleInstallView={toggleInstallView}
          isUpdateActionInProgress={!!props.isUpdateActionInProgress}
          isDeleteActionInProgress={!!props.isDeleteActionInProgress}
        />
      )
    }
  }

  const resetErrorState = () => {
    props.actions.resetRequest([
      ADD_CATALOG_APP_INSTANCE,
      DELETE_CATALOG_APP_INSTANCE,
      FETCH_CATALOG_APP_INSTANCE,
      UPDATE_CATALOG_APP_INSTANCE,
      FETCH_CATALOG_APP
    ])
  }

  return (
    <Page className="space-app-wrapper" isLoading={props.isLoading} errorsToHandle={errorsToHandle}>
      <Page.Header
        backNavTitle={
          match.path.includes('edit') ? (
            <FormattedMessage {...messages.backToInstalled} />
          ) : (
            <FormattedMessage {...messages.backToCatalog} />
          )
        }
        goBack={match.path.includes('edit') ? goBackInstalled : () => goBack(2)}
        title={<FormattedMessage {...messages.catalogApp} />}
      />
      {generateView()}
      {state.showDeleteModal && deleteModal()}
    </Page>
  )
}

SpaceCatalogAppDetailsPage.displayName = 'SpaceCatalogAppDetailsPage'

export { /* @deprecated */ SpaceCatalogAppDetailsPage }

const mapStateToProps = (state: RootState) => ({
  app: getCatalogApp(state),
  appInstance: getCatalogAppInstance(state),
  isLoading: isRequestPending(state, [FETCH_CATALOG_APP, FETCH_CATALOG_APP_INSTANCE]),
  isDeleteActionInProgress: isRequestPending(state, DELETE_CATALOG_APP_INSTANCE),
  isUpdateActionInProgress: isRequestPending(state, UPDATE_CATALOG_APP_INSTANCE),
  isInstallSuccessful: isRequestSuccessful(state, [ADD_CATALOG_APP_INSTANCE]),
  isDeleteSuccessful: isRequestSuccessful(state, [DELETE_CATALOG_APP_INSTANCE]),
  isUpdateSuccessful: isRequestSuccessful(state, [UPDATE_CATALOG_APP_INSTANCE]),
  hasError: isRequestError(state, [
    ADD_CATALOG_APP_INSTANCE,
    DELETE_CATALOG_APP_INSTANCE,
    FETCH_CATALOG_APP_INSTANCE,
    UPDATE_CATALOG_APP_INSTANCE,
    FETCH_CATALOG_APP
  ]),
  currentSpaceId: getCurrentSpaceId(state)
})

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      fetchCatalogApp,
      fetchCatalogAppInstance,
      updateCatalogAppInstance,
      addCatalogAppInstance,
      deleteCatalogAppInstance,
      clearCurrentApp,
      clearCurrentCatalogApp,
      resetRequest
    },
    dispatch
  )
})

export default connect(mapStateToProps, mapDispatchToProps)(SpaceCatalogAppDetailsPage)
